import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { Trans } from 'react-i18next';
import { MainLayout } from '../../MainLayout';
import { PageHeading } from '../../layout/PageHeading';
import { PianoIdentity } from '../../layout/PianoIdentity';
import { PrintButton } from '../../layout/PrintButton';
import { AnalysisFooter } from '@/components/analyze/key-reading/AnalysisFooter';
import { KeyReadingStrip } from '@/components/analyze/key-reading/KeyReadingStrip';
import { StrikeWeightChart } from '@/components/charts/StrikeWeightChart';
import { serieValueAt, type ChartReadingContext } from '@/components/charts/interfaces';
import { useAnalyzedKeyboard } from '@/hooks/keyboard/use-analyzed-keyboard';
import { useTranslation } from '@/hooks/use-translation';
import { formatGrams } from '@/lib/format/format-weight';
import { StrikeWeightLevel } from '@/lib/piano/touch-design/hammer-weight-level';
import { usePrintLandscape } from '@/hooks/use-print-page-size';
import { MeasureFirst } from '@/components/analyze/MeasureFirst';

/**
 * The three curves the mockups read out beside the measurement. They are
 * matched by name against the series the chart drew, hence plain strings.
 */
const REFERENCE_CURVES: readonly string[] = [StrikeWeightLevel.Level1, StrikeWeightLevel.Level5, StrikeWeightLevel.Level9];
export const AnalyzeStrikeWeightPage = () => {
  const $ = _c(20);
  usePrintLandscape();
  const analyzedKeyboard = useAnalyzedKeyboard();
  const {
    currentLanguage,
    t
  } = useTranslation();
  let t0;
  if ($[0] !== analyzedKeyboard || $[1] !== currentLanguage || $[2] !== t) {
    t0 = t1 => {
      const {
        hoveredKey,
        measuredSerie,
        referenceSeries
      } = t1;
      return <KeyReadingStrip reading={{
        keyNumber: hoveredKey,
        noteName: analyzedKeyboard.getKeyByNumber(hoveredKey ?? 0)?.name ?? null,
        rows: [{
          isAccent: true,
          label: t("Measured strike weight"),
          value: formatGrams(serieValueAt(measuredSerie, hoveredKey), currentLanguage)
        }, ...REFERENCE_CURVES.map(name => ({
          label: name,
          value: formatGrams(serieValueAt(referenceSeries.find(serie => serie.name === name), hoveredKey), currentLanguage)
        }))]
      }} />;
    };
    $[0] = analyzedKeyboard;
    $[1] = currentLanguage;
    $[2] = t;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const renderReading = t0;
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <><PianoIdentity /><PrintButton /></>;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <em className="ptl-accent">Weight</em>;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = <PageHeading title={<Trans t={t} i18nKey="Strike <1>Weight</1>">Strike {t2}</Trans>} />;
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <MeasureFirst />;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== analyzedKeyboard || $[10] !== renderReading) {
    t5 = <StrikeWeightChart emptyState={t4} keyboard={analyzedKeyboard} renderReading={renderReading} />;
    $[9] = analyzedKeyboard;
    $[10] = renderReading;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== t) {
    t6 = t("Design the strike weight");
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t6) {
    t7 = <AnalysisFooter action={{
      label: t6,
      to: "/design/strike-weight"
    }} />;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== t3 || $[17] !== t5 || $[18] !== t7) {
    t8 = <MainLayout headerRight={t1}>{t3}{t5}{t7}</MainLayout>;
    $[16] = t3;
    $[17] = t5;
    $[18] = t7;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  return t8;
};